import { useState, useEffect } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const lines = (arr) => (arr || []).join("\n");
const toArr = (s) => s.split("\n").map(x => x.trim()).filter(Boolean);
const fromPacket = (p) => ({
  cover_letter: p?.cover_letter || "", scope_summary: p?.scope_summary || "", deliverables: lines(p?.deliverables), terms: lines(p?.terms),
  timeline: lines((p?.timeline || []).map(t => `${t.phase} | ${t.detail}`)), project_fee: p?.project_fee ?? "",
  extra_items: p?.extra_items || [], admin_notes: p?.admin_notes || "",
});

const Extras = ({ items, set }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between"><Label className="text-xs">Extra invoice lines (use a negative amount for a credit)</Label>
      <button onClick={() => set([...items, { label: "", amount: 0 }])} className="text-xs text-blue-600 font-medium flex items-center gap-0.5" data-testid="packet-extra-add"><Plus className="w-3.5 h-3.5" />Add line</button></div>
    {items.map((it, i) => (
      <div key={i} className="flex gap-2">
        <Input value={it.label} onChange={e => set(items.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="e.g. Google Business Profile setup" className="h-8 text-sm" data-testid={`packet-extra-label-${i}`} />
        <Input type="number" value={it.amount} onChange={e => set(items.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="h-8 w-28 text-sm" data-testid={`packet-extra-amount-${i}`} />
        <button onClick={() => set(items.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-600" data-testid={`packet-extra-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    ))}
  </div>
);

export const PacketEditor = ({ planId, packet, onSaved }) => {
  const [f, setF] = useState(fromPacket(packet));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(fromPacket(packet)); }, [packet]);
  const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, deliverables: toArr(f.deliverables), terms: toArr(f.terms),
        timeline: toArr(f.timeline).map(l => { const [phase, ...rest] = l.split("|"); return { phase: phase.trim(), detail: rest.join("|").trim() }; }),
        project_fee: f.project_fee === "" ? null : Number(f.project_fee),
        extra_items: f.extra_items.filter(i => i.label.trim()).map(i => ({ label: i.label.trim(), amount: Number(i.amount) || 0 })) };
      const { data } = await api.put(`/api/webadvisor/plan/${planId}/packet`, body);
      onSaved(data); toast.success("Packet saved");
    } catch (err) { const d = err.response?.data?.detail; toast.error(Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || "Save failed"); }
    finally { setBusy(false); }
  };
  const T = ({ k, label, rows, hint }) => (
    <div><Label className="text-xs">{label}{hint && <span className="text-slate-400 font-normal"> · {hint}</span>}</Label>
      <Textarea rows={rows} value={f[k]} onChange={e => set(k)(e.target.value)} className="text-sm" data-testid={`packet-${k}`} /></div>
  );
  return (
    <div className="space-y-3" data-testid="packet-editor">
      {T({ k: "cover_letter", label: "Cover letter", rows: 6 })}
      {T({ k: "scope_summary", label: "Final plan — scope", rows: 3 })}
      <div className="grid md:grid-cols-2 gap-3">
        {T({ k: "deliverables", label: "Deliverables", rows: 6, hint: "one per line" })}
        {T({ k: "timeline", label: "Timeline", rows: 6, hint: "Phase | what happens" })}
      </div>
      {T({ k: "terms", label: "Contract terms", rows: 5, hint: "one per line · the payment line (terms + invoice total) is added automatically" })}
      <div className="grid md:grid-cols-3 gap-3 items-start">
        <div><Label className="text-xs">Project fee $ (blank = plan estimate)</Label><Input type="number" value={f.project_fee} onChange={e => set("project_fee")(e.target.value)} className="h-8 text-sm" data-testid="packet-project-fee" /></div>
        <div className="md:col-span-2"><Extras items={f.extra_items} set={set("extra_items")} /></div>
      </div>
      <button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="packet-save-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save edits
      </button>
    </div>
  );
};
